import type { FormEvent } from "react";
import { Link } from "react-router";
import { Alerta, Button, CheckList, MontoField, Row, Select, Stack, TextField, type OpcionCheckList } from "@/components";
import type { CompradorListado, Modalidad } from "@/lib/api";
import { OPCIONES_MODALIDAD, opcionesComprador, type ErroresVenta, type FormVenta } from "@/lib/ventas";

export interface FormularioVentaProps {
  form: FormVenta;
  cambiar: <K extends keyof FormVenta>(campo: K, valor: FormVenta[K]) => void;
  errores: ErroresVenta;
  compradores: CompradorListado[];
  /** Unidades elegibles (ver `opcionesUnidadesDisponibles`). */
  unidades: OpcionCheckList[];
  /** En edición no se cambian la modalidad ni el mes de origen del CAC. */
  edicion: boolean;
  onSubmit: (e: FormEvent) => void;
  onCancelar: () => void;
  guardando: boolean;
  error: string | null;
}

/** Campos de la venta (alta y edición). Sin `<Dialog>`: lo envuelven `VentasTab` y la ficha de la venta. */
export function FormularioVenta({ form, cambiar, errores, compradores, unidades, edicion, onSubmit, onCancelar, guardando, error }: FormularioVentaProps) {
  const conCac = form.modalidad === "ARS_CAC";
  return (
    <form onSubmit={onSubmit} noValidate>
      <Stack espacio={4}>
        <Stack espacio={1}>
          <Select
            etiqueta="Comprador"
            opciones={opcionesComprador(compradores)}
            valor={form.comprador_id || undefined}
            onCambio={(v) => cambiar("comprador_id", v)}
            placeholder="Elegí el comprador"
            error={errores.comprador_id}
          />
          <Link to="/compradores" className="text-chico text-primario underline-offset-2 hover:underline">
            Cargar un comprador nuevo
          </Link>
        </Stack>
        <Row espacio={3} envolver>
          <TextField etiqueta="Fecha" type="date" value={form.fecha} onChange={(e) => cambiar("fecha", e.target.value)} error={errores.fecha} contenedorClassName="w-44" />
          <Select
            etiqueta="Modalidad"
            opciones={OPCIONES_MODALIDAD}
            valor={form.modalidad}
            onCambio={(v) => cambiar("modalidad", v as Modalidad)}
            deshabilitado={edicion}
            className="min-w-56 flex-1"
          />
        </Row>
        <MontoField
          etiqueta="Precio"
          valor={form.precio}
          onCambio={(v) => cambiar("precio", v)}
          moneda={form.modalidad === "USD" ? "USD" : "ARS"}
          error={errores.precio}
          ayuda={conCac ? "En pesos al índice de origen" : undefined}
        />
        {conCac && !edicion && (
          <TextField
            etiqueta="Mes de origen del CAC"
            placeholder="AAAA-MM"
            inputMode="numeric"
            ayuda="Tiene que estar cargado en Ventas → Índice CAC"
            value={form.cac_origen_periodo}
            onChange={(e) => cambiar("cac_origen_periodo", e.target.value)}
            error={errores.cac_origen_periodo}
            contenedorClassName="w-56"
          />
        )}
        <CheckList
          etiqueta="Unidades"
          opciones={unidades}
          valores={form.unidad_ids}
          onCambio={(v) => cambiar("unidad_ids", v)}
          error={errores.unidad_ids}
          vacio="No hay unidades disponibles"
        />
        <TextField etiqueta="Notas" ayuda="Opcional" value={form.notas} onChange={(e) => cambiar("notas", e.target.value)} error={errores.notas} />
        <Alerta texto={error} />
        <Row justificar="fin" espacio={2}>
          <Button variante="secundario" onClick={onCancelar}>
            Cancelar
          </Button>
          <Button type="submit" cargando={guardando}>
            Guardar
          </Button>
        </Row>
      </Stack>
    </form>
  );
}
